<template>
    <Layout is-crm>
        <div class="page__top">
            <h1>{{ $t('crm.orders.title') }}</h1>
            <div class="orders__actions">
                <button
                    class="button button--big button--blank"
                    type="button"
                    :disabled="exporting"
                    @click="exportCsv"
                >
                    {{ $t('crm.orders.export.button') }}
                </button>
                <WithGlobalPermission permission="Order.create">
                    <router-link
                        href="/orders/create"
                        class="button button--big"
                        to="/orders/create"
                    >
                        Create an order
                    </router-link>
                </WithGlobalPermission>
            </div>
        </div>

        <PaginatedResource
            ref="ordersResource"
            :fetch-method="fetchOrders"
            :filter-fields="[
                'customer',
                'type_productions',
                'modified__range',
                'state',
                'speaker',
                'is_active',
                'need_attention',
                'order_type',
            ]"
            :sorting-fields="['modified', 'customer', 'type_productions']"
            delay-fetch
            filter-key="orders"
            @filterUpdate="onFilterUpdate"
        >
            <template
                #default="{
                    loading,
                    sort,
                    search,
                    filter,
                    canLoadMore,
                    resource: orders,
                    isEmpty,
                    notFound,
                    filters,
                    loadMore,
                }"
            >
                <div class="orders">
                    <Spinner v-if="loading" />
                    <OrdersFilters
                        v-if="!isEmpty"
                        class="orders__filters"
                        :filters="filters"
                        :tabs="tabs.tabs"
                        :customers="customers"
                        :types="types"
                        :states="states"
                        :production-types="productionTypes"
                        :speakers="speakers"
                        @search="search"
                        @filter="filter"
                    />
                    <OrdersTable
                        v-if="!isEmpty"
                        class="orders__table"
                        :orders="orders"
                        @sort="sort"
                    />
                    <div v-if="notFound && !isEmpty" class="empty">
                        {{ $t('crm.orders.list.notFound') }}
                    </div>
                    <div v-if="isEmpty" class="empty">
                        <p style="font-size: 20px">
                            {{ $t('crm.orders.list.empty') }}
                        </p>
                    </div>
                    <div v-if="canLoadMore" class="orders__loadmore">
                        <button
                            class="button button--big button--blank"
                            type="button"
                            @click="loadMore"
                        >
                            {{ $t('misc.loadmore') }}
                        </button>
                    </div>
                </div>
            </template>
        </PaginatedResource>
    </Layout>
</template>

<script>
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import { WithGlobalPermission } from '@speechki-frontend/core/auth/components';

import { OrdersTable, OrdersFilters } from '../../components';

import {
    fetchOrders,
    fetchCustomer,
    fetchProductionTypes,
    fetchOrdersTabsSettings,
    fetchOrderTypes,
    exportOrdersCsv,
} from '../../api/orders';
import { fetchSpeakers } from '@speechki-frontend/core/editor/api/speakers';

import { removeEmptyFields } from '@speechki-frontend/shared/helpers/functions';
import { mapGetters } from 'vuex';

const tabsFilterMap = {
    active: {
        is_active: true,
        need_attention: false,
    },
    all: {
        is_active: false,
        need_attention: false,
    },
    need_attention: {
        need_attention: true,
        is_active: false,
    },
};

const getTabFilter = (tab) => tabsFilterMap[tab];

const pad = (value) => String(value).padStart(2, '0');

const formatFileDate = (date) => {
    const day = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
    return `${day}_${pad(date.getHours())}${pad(date.getMinutes())}`;
};

const downloadFile = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export default {
    name: 'OrdersPage',
    components: {
        Layout,
        Spinner,
        PaginatedResource,
        WithGlobalPermission,

        OrdersTable,
        OrdersFilters,
    },

    inject: ['registerWsHandler', 'removeWsHandler'],

    data() {
        return {
            customers: [],
            productionTypes: [],
            states: [],
            types: [],
            speakers: [],
            exporting: false,
            tabs: {
                default: '',
                tabs: [],
            },
        };
    },

    computed: {
        ...mapGetters(['isAdmin']),
        ...mapGetters('workspaces', ['workspaceCustomerId', 'isInnerWorkspace']),
    },

    watch: {
        workspaceCustomerId(customer) {
            this.$refs.ordersResource.filter({ customer, state: null });
            this.fetchOrdersTabsSettings(customer);
        },
    },

    beforeUnmount() {
        this.removeWsHandler('order_change_state', this.onOrderChangeState);
    },

    async mounted() {
        const { ordersResource } = this.$refs;
        this.registerWsHandler('order_change_state', this.onOrderChangeState);

        await this.prepareData();

        ordersResource.filter({
            customer: this.workspaceCustomerId,
            ...getTabFilter(this.tabs.default),
        });
    },

    methods: {
        fetchOrders,

        async prepareData() {
            const promises = [];

            promises.push(this.fetchOrdersTabsSettings());
            promises.push(this.fetchOrderTypes());

            if (this.isAdmin || this.workspaceCustomerId) {
                this.customers = this.$store.state.workspaces.workspaces;

                promises.push(this.fetchProductionTypes(), this.fetchSpeakers());
            }

            await Promise.all(promises);
        },
        async fetchOrdersTabsSettings() {
            this.tabs = await fetchOrdersTabsSettings(this.workspaceCustomerId);
        },
        async fetchProductionTypes() {
            const result = await fetchProductionTypes();
            this.productionTypes = Object.entries(result).map(([key, value]) => ({
                id: key,
                name: value,
            }));
        },
        async fetchOrderTypes() {
            const types = await fetchOrderTypes();
            this.types = Object.entries(types).map(([key, value]) => ({
                id: key,
                name: value,
            }));
        },
        async fetchSpeakers() {
            this.speakers = await fetchSpeakers();
        },
        async onFilterUpdate(diff) {
            if (diff.customer) {
                if (diff.customer.new) {
                    const customer = await fetchCustomer(diff.customer.new);
                    if (customer && customer.states) {
                        this.states = customer.states;
                    }
                    return;
                }

                if (this.isInnerWorkspace) {
                    this.$refs.ordersResource.filter({ state: null });
                }
                this.states = [];
            }
        },

        async exportCsv() {
            const { filters, searchString, ordering } = this.$refs.ordersResource;
            const params = removeEmptyFields(filters);

            if (searchString) params.search = searchString;
            if (ordering) params.ordering = ordering;

            this.exporting = true;
            try {
                const csv = await exportOrdersCsv(params);
                // 5xx resolves to undefined: the axios interceptor already showed a toast
                if (!csv) return;
                downloadFile(csv, `orders_report_${formatFileDate(new Date())}.csv`);
            } catch {
                this.$store.dispatch('pushToast', {
                    id: Date.now(),
                    type: 'error',
                    title: this.$t('crm.orders.export.error'),
                    text: this.$t('crm.orders.export.tryAgain'),
                });
            } finally {
                this.exporting = false;
            }
        },

        onOrderChangeState({ code, params, errors }) {
            const { order } = params;
            const { resource = [] } = this.$refs.ordersResource;

            if (code !== 200) {
                console.log(errors);
                return;
            }

            if (resource.some(({ id }) => id === order.id)) {
                this.$refs.ordersResource.fetch();
            }
        },
    },
};
</script>

<style lang="scss" src="./OrdersPage.scss"></style>
